import { getCloudFrontAssetUrl } from '@utils/asset-utils';
import type { StageId } from '@micro-screens/src/types/stage-theme';
import { STAGE_THEME_COLORS } from '@micro-screens/theme/colors';

import './instructions-screen.css';

const STAGE_OVERLAYS: Record<StageId, string> = {
  1: getCloudFrontAssetUrl('stage-1/Stage_1.png'),
  2: getCloudFrontAssetUrl('stage-2/Stage_2.png'),
  3: getCloudFrontAssetUrl('stage-3/Stage_3.png'),
  4: getCloudFrontAssetUrl('stage-4/Stage_4.png'),
  5: getCloudFrontAssetUrl('stage-5/Stage_5.png'),
  6: getCloudFrontAssetUrl('stage-6/Stage_6.png'),
  7: getCloudFrontAssetUrl('stage-7/Stage_7.png'),
};

interface GameInstructionsSkeletonProps {
  stage: StageId;
}

export function GameInstructionsSkeleton({ stage }: Readonly<GameInstructionsSkeletonProps>) {
  const theme = STAGE_THEME_COLORS[stage];
  const overlay = STAGE_OVERLAYS[stage];

  return (
    <div className="instructionViewport">
      <section
        className="instructionRoot"
        style={{ backgroundColor: theme.background }}
        aria-busy="true"
        aria-label="Loading game instructions"
      >
        <img src={overlay} alt="" aria-hidden className="overlay" />
        <div
          className="eclipse"
          style={{ '--eclipse-color': theme.eclipse } as React.CSSProperties}
        />

        <div className="content">
          <div className="game-header">
            <div className="metaRow">
              <span className="skeletonBar" style={{ height: 14, width: 90, display: 'block' }} />
              <span
                className="skeletonBar"
                style={{ height: 20, width: 60, borderRadius: 999, display: 'block' }}
              />
            </div>
            <div className="skeletonBar" style={{ height: 22, width: '65%', marginTop: 10 }} />
          </div>

          <div
            className="skeletonBar"
            style={{ height: 30, width: '48%', marginTop: 20, marginBottom: 16 }}
          />

          <div className="movesScroll">
            <ol className="moveList">
              {[0, 1, 2].map((moveIndex) => (
                <li className="move" key={moveIndex}>
                  <div className="moveHeader">
                    <span
                      className="moveMarker skeletonBar"
                      style={{ animationDelay: `${moveIndex * 120}ms` }}
                    />
                    <span className="skeletonBar" style={{ height: 14, width: 74, display: 'block' }} />
                    <span
                      className="skeletonBar"
                      style={{ height: 18, width: 62, borderRadius: 6, display: 'block' }}
                    />
                  </div>

                  <div className="moveCard">
                    <div className="moveCardHead">
                      <span
                        className="moveIcon skeletonBar"
                        style={{ background: 'rgba(255,255,255,0.12)' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="skeletonBar" style={{ height: 16, width: '62%' }} />
                        <div
                          className="skeletonBar"
                          style={{ height: 12, width: '90%', marginTop: 8, opacity: 0.7 }}
                        />
                      </div>
                    </div>

                    {[0, 1].map((pointIndex) => (
                      <div key={pointIndex} style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <div
                          className="skeletonBar"
                          style={{
                            width: 4,
                            height: 4,
                            marginTop: 8,
                            borderRadius: 999,
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div className="skeletonBar" style={{ height: 13, width: '45%' }} />
                          <div
                            className="skeletonBar"
                            style={{ height: 12, width: '78%', marginTop: 6, opacity: 0.6 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="ctaStack">
            <div
              className="skeletonBar"
              style={{
                height: 54,
                borderRadius: 100,
                background: 'rgba(255,212,132,0.25)',
                animationDelay: '0ms',
              }}
            />
            <div
              className="skeletonBar"
              style={{
                height: 54,
                borderRadius: 100,
                background: 'rgba(255,255,255,0.12)',
                animationDelay: '200ms',
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
